import type { TimeSeries, TimeSeriesPoint } from '@/shared/charts/time-series-chart/TimeSeriesChart.types';
import { findGapIndexes } from '@/shared/charts/time-series-chart/model/findGapIndexes';
import { fitsBucketBudget, selectMinMaxIndices } from '@/shared/charts/time-series-chart/model/selectMinMaxIndices';

import { lineDrawingBudget } from '../../config/chartVisuals';
import type { TimeDomain } from '../../utils/timeDomain';
import { findLonePoints, toLineData } from './lineData';
import type { LineDatum, LinePoint } from './lineData';
import { getWindowIndexRange, spliceWindowIndexes } from './windowIndexes';

/**
 * What each line hands to ECharts, cached per points array: an unchanged series is never converted
 * twice, and ECharts keeps getting the same arrays, which lets it skip re-parsing them on setOption.
 * A new fetch brings a new array and so a fresh entry; the old one goes away with its array.
 */

/** What a line is drawn from: its points and where it breaks (see TimeSeries.gapThresholdMs). */
export type LineSource = Pick<TimeSeries, 'points' | 'gapThresholdMs'>;

export type LineSeriesData = {
  data: LineDatum[];
  lonePoints: LinePoint[];
  /** Of the whole series, so the value axis does not rescale as the window moves. */
  minValue: number;
  maxValue: number;
};

type Overview = {
  indexes: number[];
  data: LineSeriesData;
};

type DrawnWindow = {
  visibleRange: TimeDomain;
  bucketCount: number;
  data: LineSeriesData;
};

type CacheEntry = {
  points: TimeSeriesPoint[];
  gapThresholdMs: number | undefined;
  /** One tuple per point, shared by every drawn variant. */
  tuples: LinePoint[];
  gapIndexes: number[];
  minValue: number;
  maxValue: number;
  full?: LineSeriesData;
  /** By bucket count: the full-view overview and the coarser context around a zoomed window. */
  overviews: Map<number, Overview>;
  /** So an unrelated rebuild (a highlight toggle) hands ECharts the same array again. */
  lastWindow?: DrawnWindow;
};

const cache = new WeakMap<TimeSeriesPoint[], CacheEntry>();

function getValueRange(points: TimeSeriesPoint[]) {
  // A loop, not Math.min(...values): spreading a long series overflows the call stack.
  let minValue = Number.POSITIVE_INFINITY;
  let maxValue = Number.NEGATIVE_INFINITY;

  for (const { value } of points) {
    minValue = Math.min(minValue, value);
    maxValue = Math.max(maxValue, value);
  }

  return points.length > 0 ? { minValue, maxValue } : { minValue: Number.NaN, maxValue: Number.NaN };
}

function getCacheEntry({ points, gapThresholdMs }: LineSource): CacheEntry {
  const cached = cache.get(points);

  if (cached && cached.gapThresholdMs === gapThresholdMs) {
    return cached;
  }

  const entry: CacheEntry = {
    points,
    gapThresholdMs,
    tuples: points.map(({ timestamp, value }): LinePoint => [timestamp, value]),
    gapIndexes: findGapIndexes(points, gapThresholdMs),
    ...getValueRange(points),
    overviews: new Map(),
  };

  cache.set(points, entry);

  return entry;
}

function drawIndexes({ tuples, gapIndexes, minValue, maxValue }: CacheEntry, indexes: number[]): LineSeriesData {
  const data = toLineData(tuples, gapIndexes, indexes);

  return { data, lonePoints: findLonePoints(data), minValue, maxValue };
}

function getFull(entry: CacheEntry): LineSeriesData {
  entry.full ??= drawIndexes(entry, Array.from(entry.tuples.keys()));

  return entry.full;
}

function getOverview(entry: CacheEntry, bucketCount: number): Overview {
  const cached = entry.overviews.get(bucketCount);

  if (cached) {
    return cached;
  }

  const indexes = selectMinMaxIndices(entry.points, 0, entry.points.length, bucketCount);
  const overview = { indexes, data: drawIndexes(entry, indexes) };

  // Only the current plot width's two budgets are ever in use.
  if (entry.overviews.size >= 2) {
    entry.overviews.clear();
  }
  entry.overviews.set(bucketCount, overview);

  return overview;
}

function getContextBucketCount(bucketCount: number): number {
  return Math.max(
    lineDrawingBudget.minContextBucketCount,
    Math.floor(bucketCount * lineDrawingBudget.contextBucketRatio)
  );
}

function isSameWindow(window: DrawnWindow | undefined, visibleRange: TimeDomain, bucketCount: number) {
  return (
    window?.bucketCount === bucketCount &&
    window.visibleRange[0] === visibleRange[0] &&
    window.visibleRange[1] === visibleRange[1]
  );
}

/** Every point of the series. */
export function getLineSeriesData(source: LineSource): LineSeriesData {
  return getFull(getCacheEntry(source));
}

/** The whole series thinned to the drawing budget; a series within budget comes back whole. */
export function getOverviewLineSeriesData(source: LineSource, bucketCount: number): LineSeriesData {
  const entry = getCacheEntry(source);

  return fitsBucketBudget(entry.points.length, bucketCount) ? getFull(entry) : getOverview(entry, bucketCount).data;
}

/**
 * What the chart draws for one line: at most the drawing budget, however dense the raw data is,
 * because ECharts re-processes every point it holds on each zoom and pan step. The visible window
 * is thinned in detail; the rest of the series comes from a coarser overview, so the line still
 * reaches both plot edges while the user pans. Data within budget passes through whole.
 */
export function getRenderableLineSeriesData(
  source: LineSource,
  visibleRange: TimeDomain,
  bucketCount: number
): LineSeriesData {
  const entry = getCacheEntry(source);

  if (fitsBucketBudget(entry.points.length, bucketCount)) {
    return getFull(entry);
  }

  if (entry.lastWindow && isSameWindow(entry.lastWindow, visibleRange, bucketCount)) {
    return entry.lastWindow.data;
  }

  const window = getWindowIndexRange(entry.points, visibleRange);

  if (window.from === 0 && window.to === entry.points.length) {
    return getOverview(entry, bucketCount).data;
  }

  const contextIndexes = getOverview(entry, getContextBucketCount(bucketCount)).indexes;
  const detailIndexes = selectMinMaxIndices(entry.points, window.from, window.to, bucketCount);
  const data = drawIndexes(entry, spliceWindowIndexes(contextIndexes, window, detailIndexes));

  entry.lastWindow = { visibleRange, bucketCount, data };

  return data;
}
